/*
 * 释放客户页面 (独立页面，通过路由 /#/releaseLead?leadId= 进入)
 *
 * 本页面是按揭贷款模块的"释放客户"功能页面，用于：
 *   - 查看客户贷款详情和还款计划（树形结构：贷款节点 → 还款计划子节点）
 *   - 填写释放表单（利率、抽佣、释放日期、客户补充信息）
 *   - 计算利息（按贷款类型 MORTGAGE/CARD/BORROW 分别计算）
 *   - 确认释放（更新客户状态、保存利息、保存银行放款记录）
 *   - 取消释放（回滚客户状态、清除利息数据）
 *
 * === API调用链路 ===
 *
 * 1. 加载释放详情
 *    GET /api/mis/release/releaseDetail?leadId=
 *    → ReleaseLeadController.getReleaseDetail
 *    → ReleaseLeadService.getReleaseDetail
 *      → leadMapper.selectById(leadId)                        获取客户信息
 *      → ReleaseLeadService.queryLoanPlanTree(leadId)         获取贷款计划树
 *        → repaymentPlanMapper.selectRepaymentPlanPage         查询所有还款计划
 *        → 按loanId分组 → 构建LoanPlanTreeDTO树形结构          返回 List<LoanPlanTreeDTO>
 *      → bankLendingMapper.selectList(by leadId)               获取银行放款信息
 *    ← ReleaseDetailDTO { lead, loanPlans, bankLendings, totalInterest, totalCommission }
 *
 * 2. 计算利息（预览，不保存）
 *    POST /api/mis/release/calculateInterest
 *    → ReleaseLeadController.calculateInterest
 *    → ReleaseLeadService.calculateInterestForLead(leadId, settledLoanAt, interest, postingInterest, borrowInterest, commission)
 *      → leadMapper.selectById → 校验利率
 *      → 遍历客户所有贷款(MortgageLoan)和还款计划(RepaymentPlan)
 *      → calculatePlanInterest 按类型计算:
 *        MORTGAGE: 代还利息 = paidAmount × (释放日-还款日+1) × 日利率
 *        CARD:     代还利息(同上) + 过账利息 = postingAmount × 过账利率
 *        BORROW:   借款利息 = oneTermAmount × (计息天数) × 借款日利率, 按期串联
 *      → 抽佣 = 利息 × 抽佣比例
 *    ← InterestCalculationResult { totalInterest, totalCommission, loanDetails[{loanId, platform, interest, commission, planDetails}] }
 *
 * 3. 确认释放
 *    POST /api/mis/release/releaseWithInterest
 *    → ReleaseLeadController.releaseWithInterest
 *    → ReleaseLeadService.releaseLeadWithInterest(request: ReleaseLeadReq)
 *      → 校验客户存在且未释放
 *      → calculateInterestForLead 计算利息
 *      → leadMapper.updateById: 更新 lead 的 settledLoanAt, loanStatus=SETTLED, 利率, 抽佣, 补充字段
 *      → updateLoanInterest: 更新 mortgage_loan 的 interestAmount, commissionAmount, endRateDate
 *      → updatePlanInterest: 更新 repayment_plan 的 interestAmount, commissionAmount
 *      → saveBankLendings: 先删后增 bank_lending 记录
 *    ← ReleaseResult { leadId, totalInterest, totalCommission, releaseDate }
 *
 * 4. 取消释放
 *    POST /api/mis/release/cancelRelease
 *    → ReleaseLeadController.cancelRelease
 *    → ReleaseLeadService.cancelReleaseLead(leadId)
 *      → leadMapper.update: 清空 settledLoanAt/settledLoanOperator, loanStatus→LOANING
 *      → resetInterestByLeadId: 清空 repayment_plan 和 mortgage_loan 的利息/抽佣/endRateDate
 *    ← "取消释放成功"
 *
 * === 子组件 ===
 *   ReleaseForm     - 客户信息表单（利率、抽佣、释放日期、客户补充信息）
 *   LoanPlanTable   - 贷款计划树形表格（贷款节点 → 还款计划子节点，支持搜索/筛选）
 *   BankLendingTable- 银行放款记录表格（行内编辑CRUD，数据随释放一起提交）
 *   InterestCalculator - 利息计算器弹窗（当前为占位实现，使用mock数据）
 *   ReleaseSummary  - 释放汇总卡片（贷款笔数、金额、利息、抽佣）
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card,
    Typography,
    Button,
    Modal,
    Toast,
    Tag,
    Row,
    Col,
    Divider,
    Space,
    Descriptions,
    Timeline,
    Badge,
    Tooltip,
    Tabs,
    TabPane
} from '@douyinfe/semi-ui';
import {
    IconCalendar,
    IconCheckCircleStroked,
    IconClose,
    IconBeaker,
    IconCoinMoneyStroked,
    IconEyeOpened,
    IconEyeClosed,
    IconEdit,
    IconDelete,
    IconRefresh,
    IconArrowLeft,
    IconDownload,
    IconRestart,
    IconUserCircle,
    IconServer
} from '@douyinfe/semi-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { formatAmountZhCn,formatAmountZhCnZero1, formatDate,   } from "../../utils";

// 子组件: 客户信息表单(利率/抽佣/释放日期/补充字段), 内部通过 onFormApiReady 暴露 formApi 供父组件读取表单值
import ReleaseForm from './components/ReleaseForm';
// 子组件: 贷款计划树形表格(贷款节点展开显示还款计划子节点), 支持按平台/贷款类型搜索筛选
import LoanPlanTable from './components/LoanPlanTable';
// 子组件: 银行放款记录表格(行内编辑CRUD), 数据通过 onChange 回调同步到父组件 bankLendings 状态
import BankLendingTable from './components/BankLendingTable';
// 子组件: 利息计算器弹窗(当前为占位mock实现, 实际利息计算通过 handleCalculateInterest 调用后端)
import InterestCalculator from './components/InterestCalculator';
// 子组件: 释放汇总卡片, 显示贷款笔数/金额/利息/抽佣等汇总数据
import ReleaseSummary from './components/ReleaseSummary';
import './ReleaseLeadPage.scss';
import { ApiClient } from '../../api/ApiClient';
import ReconciliationByLead from "./indexReconciliationByLead";


const { Title, Text } = Typography;

// 页面Props: 可通过组件属性传入 leadId，也可通过路由参数 /releaseLead/:id 获取
interface ReleaseLeadPageProps {
    leadId?: number;
}

const ReleaseLeadPage: React.FC<ReleaseLeadPageProps> = ({ leadId: propLeadId }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    // leadId 优先取 props 传入值，其次取路由参数
    const leadId = propLeadId || id;

    // 页面状态
    const [loading, setLoading] = useState(false);
    // 当前激活的标签页: 'loans'(贷款详情) | 'bank'(银行放款)
    const [activeTab, setActiveTab] = useState('reconciliation');
    // 利息计算器弹窗显示状态
    const [showCalculator, setShowCalculator] = useState(false);
    // 释放成功预览弹窗显示状态
    const [showPreview, setShowPreview] = useState(false);

    // 数据状态
    // 客户信息(Lead对象), 包含 name, agentName, settledLoanAt, interest, commission 等
    const [leadInfo, setLeadInfo] = useState<any>(null);
    // 贷款计划树形数据, 每个元素是 LoanPlanTreeDTO (含 childrenNodes 为还款计划子节点)
    const [loanPlans, setLoanPlans] = useState<any[]>([]);
    // 银行放款记录, 用于编辑后随释放请求一起提交
    const [bankLendings, setBankLendings] = useState<any[]>([]);
    // 利息计算结果, 包含 totalInterest, totalCommission, loanDetails
    const [interestResult, setInterestResult] = useState<any>(null);
    // 释放详情原始数据(lead + loanPlans + bankLendings)
    const [releaseDetail, setReleaseDetail] = useState<any>(null);
    // 按出款人统计利息(含手动填写的实际利息)
    const [payerInterestList, setPayerInterestList] = useState<any[]>([]);
    // 已保存的出款人利息记录(从releaseDetail加载)
    const [savedLeadExtList, setSavedLeadExtList] = useState<any[]>([]);
    // 按出款人统计利息的tab: 'ck'(出款利息) | 'gz'(过账利息)
    const [payerInterestTab, setPayerInterestTab] = useState('ck');

    // ReleaseForm 的 formApi 引用, 用于在计算利息/确认释放时读取表单值
    const [releaseFormApi, setReleaseFormApi] = useState<any>(null);
    let apiClient = ApiClient.getDefault();
    const [releaseDate, setReleaseDate] = useState('');

    // 页面初始化: leadId变化时重新加载数据
    useEffect(() => {
        if (leadId) {
            loadData();
        }
    }, [leadId]);

    /**
     * 加载释放详情数据
     * 调用链: GET /api/mis/release/releaseDetail?leadId=
     * → ReleaseLeadController.getReleaseDetail → ReleaseLeadService.getReleaseDetail
     *   → leadMapper.selectById + queryLoanPlanTree + selectBankLendingByLeadId
     * ← ReleaseDetailDTO { lead, loanPlans: LoanPlanTreeDTO[], bankLendings: BankLending[] }
     */
    const loadData = async () => {
        if (!leadId) return;

        try {
            setLoading(true);

            // 1. 加载客户释放详情(包含客户信息、贷款计划树、银行放款记录)
            const leadRes:any = await apiClient.request('/release/releaseDetail','get',{leadId:Number(leadId)});
            if (leadRes.code == "ok" && leadRes.data) {
                const { lead, loanPlans: plans, bankLendings: lendings, leadExtList } = leadRes.data;
                setLeadInfo(lead);
                setLoanPlans(plans || []);
                setBankLendings(lendings || []);
                setReleaseDetail(leadRes.data);
                // 加载已保存的出款人利息记录
                if (leadExtList && leadExtList.length > 0) {
                    setSavedLeadExtList(leadExtList);
                    setPayerInterestList(leadExtList.map((ext: any) => ({
                        outMoneyId: ext.outMoneyId,
                        outMoneyName: ext.outMoneyName,
                        outMoneyAmount: ext.outMoneyAmount,
                        creditOutMoneyAmount: ext.creditOutMoneyAmount,
                        cardOutMoneyAmount: ext.cardOutMoneyAmount,
                        cardPostingOutMoneyAmount: ext.cardPostingOutMoneyAmount,
                        borrowOutMoneyAmount: ext.borrowOutMoneyAmount,
                        outMoneyCount: ext.outMoneyCount,
                        outMoneyCountCk: ext.outMoneyCountCk,
                        outMoneyCountGz: ext.outMoneyCountGz,
                        interestDays: ext.interestDays,
                        interestAmount: ext.interestAmount,
                        interestAmountCk: ext.interestAmountCk,
                        interestAmountGz: ext.interestAmountGz,
                        realInterestAmount: ext.realInterestAmount || 0,
                    })));
                }
            }

            // 2. 检查是否已释放，提示用户
            if (leadInfo?.settledLoanAt) {
                Toast.info('该客户已释放，可查看释放详情');
            }

        } catch (error) {
            console.error('加载数据失败:', error);
            Toast.error('加载数据失败');
        } finally {
            setLoading(false);
        }
    };

    /**
     * 计算汇总数据(内存中根据 loanPlans 计算，不调后端)
     * 汇总: 总贷款金额、总已还金额、总利息、总抽佣、贷款笔数、计划总数
     */
    const summaryData = useMemo(() => {
        if (!loanPlans || loanPlans.length === 0) {
            return {
                totalLoanAmount: 0,
                totalPaidAmount: 0,
                totalInterest: 0,
                totalCommission: 0,
                loanCount: 0,
                planCount: 0
            };
        }

        let planCount = 0;
        const summary = {
            totalLoanAmount: 0,
            totalPaidAmount: 0,
            totalInterest: 0,
            totalCommission: 0,
            loanCount: loanPlans.length,
            planCount: 0
        };

        // 遍历贷款节点累加汇总
        loanPlans.forEach(loan => {
            summary.totalLoanAmount += loan.amount || 0;
            summary.totalPaidAmount += loan.paidAmount || 0;
            summary.totalInterest += loan.interest || 0;
            summary.totalCommission += loan.commission || 0;

            if (loan.childrenNodes) {
                planCount += loan.childrenNodes.length;
            }
        });

        summary.planCount = planCount;
        return summary;
    }, [loanPlans]);

    /**
     * 处理利息计算(从 ReleaseForm 读取利率参数，调用后端计算)
     * 调用链: POST /api/mis/release/calculateInterest
     * → ReleaseLeadController.calculateInterest
     * → ReleaseLeadService.calculateInterestForLead(leadId, settledLoanAt, interest, postingInterest, borrowInterest, commission)
     *   → 遍历贷款和还款计划，按 MORTGAGE/CARD/BORROW 三种类型分别计算利息
     *   → MORTGAGE: 代还利息 = paidAmount × 天数 × 日利率
     *   → CARD: 代还利息 + 过账利息(postingAmount × 过账利率)
     *   → BORROW: 借款利息 = oneTermAmount × 天数 × 借款日利率
     *   → 抽佣 = 利息 × 抽佣比例
     * ← InterestCalculationResult { totalInterest, totalCommission, loanDetails[{loanId, platform, interest, commission, planDetails}] }
     */
    const handleCalculateInterest = async () => {
        if (!releaseFormApi) {
            Toast.warning('请先填写表单');
            return;
        }

        try {
            // 从 ReleaseForm 读取表单值
            const values = await releaseFormApi.validate();

            // 构建利息计算请求参数
            const request = {
                leadId: Number(leadId),
                settledLoanAt: formatDate(values.settledLoanAt),  // 释放日期，作为计息截止日
                interest: values.interest,                         // 日利率(MORTGAGE/CARD代还使用)
                borrowInterest: values.borrowInterest,             // 借款日利率(BORROW使用)
                postingInterest: values.postingInterest,           // 过账利率(CARD过账使用)
                commission: values.commission                      // 抽佣比例
            };

            // 赋值释放日期
            setReleaseDate(formatDate(values.settledLoanAt));

            setLoading(true);
            const res:any = await apiClient.request('/release/calculateInterest', 'post', null, request);

            if (res.code == "ok" && res.data) {
                setInterestResult(res.data);

                // 按出款人汇总利息，合并已保存的实际利息
                if (res.data.payerInterestList) {
                    setPayerInterestList(res.data.payerInterestList.map((p: any) => {
                        let saved = savedLeadExtList.find((s: any) => s.outMoneyId === p.outMoneyId);
                        return {
                            ...p,
                            realInterestAmount: saved?.realInterestAmount || 0,
                        };
                    }));
                }

                // 将计算结果更新到贷款计划表格中(前端更新显示，不保存到数据库)
                const updatedPlans = updatePlansWithInterest(res.data);
                setLoanPlans(updatedPlans);

                Toast.success('利息计算完成');
                setShowCalculator(false);
            } else {
                Toast.error(res.msg || '计算失败');
            }
        } catch (error) {
            console.error('计算利息失败:', error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * 将后端利息计算结果更新到前端 loanPlans 状态中(仅更新显示，不写库)
     * 遍历 interestResult.loanDetails，匹配 loanId 更新利息/抽佣
     * 遍历 loanDetail.planDetails，匹配 planId 更新每期计划的利息/抽佣/计息天数
     */
    const updatePlansWithInterest = (interestData: any) => {
        if (!interestData?.loanDetails || !loanPlans) return loanPlans;

        return loanPlans.map(loan => {
            loan.interest = 0;
            loan.commission = 0;
            const loanDetail = interestData.loanDetails.find(
                (detail: any) => detail.loanId === loan.loanId
            );

            if (loanDetail) {
                const updatedLoan = { ...loan };
                // 更新贷款级别的利息和抽佣
                updatedLoan.interest = loanDetail.interest;
                updatedLoan.commission = loanDetail.commission;

                // 更新子节点(还款计划)的利息和抽佣
                if (updatedLoan.childrenNodes && loanDetail.planDetails) {
                    updatedLoan.childrenNodes = updatedLoan.childrenNodes.map((plan: any) => {
                        const planDetail = loanDetail.planDetails.find(
                            (p: any) => p.planId === plan.planId
                        );

                        if (planDetail) {
                            return {
                                ...plan,
                                planInterest: planDetail.interest,       // 计划利息
                                planCommission: planDetail.commission,   // 计划抽佣
                                interestDays: planDetail.interestDays    // 计息天数
                            };
                        }
                        return plan;
                    });
                }

                return updatedLoan;
            }

            return loan;
        });
    };

    /**
     * 处理释放客户(确认释放按钮)
     * 调用链: POST /api/mis/release/releaseWithInterest
     * → ReleaseLeadController.releaseWithInterest
     * → ReleaseLeadService.releaseLeadWithInterest(request: ReleaseLeadReq)
     *   → 校验客户存在且未释放
     *   → calculateInterestForLead 重新计算利息
     *   → leadMapper.updateById: 更新客户状态(loanStatus=SETTLED)、释放日期、利率、抽佣、补充字段
     *   → updateLoanInterest: 批量更新 mortgage_loan 的 interestAmount, commissionAmount, endRateDate
     *   → updatePlanInterest: 批量更新 repayment_plan 的 interestAmount, commissionAmount
     *   → saveBankLendings: 先删除旧银行放款记录，再插入新记录
     *   → logReleaseOperation: 记录操作日志
     * ← ReleaseResult { leadId, totalInterest, totalCommission, releaseDate }
     */
    const handleRelease = async () => {
        if (!releaseFormApi) return;

        Modal.confirm({
            title: '确认释放客户',
            content: '释放客户将计算所有利息并更新数据，此操作不可逆，确认继续吗？',
            okText: '确认释放',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                try {
                    // 从 ReleaseForm 读取表单值
                    const values = await releaseFormApi.validate();

                    // 构建释放请求参数(包含表单值 + 银行放款记录)
                    const request = {
                        ...values,
                        leadId: Number(leadId),
                        settledLoanAt: formatDate(values.settledLoanAt),  // 释放日期
                        settledLoanOperator: values.settledLoanOperator,  // 经办人
                        interest: values.interest,                         // 日利率
                        borrowInterest: values.interest,                   // 借款日利率(注意:这里用的interest，可能是bug)
                        postingInterest: values.interest,                  // 过账利率(注意:这里用的interest，可能是bug)
                        commission: values.commission,                     // 抽佣比例
                        // 银行放款记录，格式化日期字段
                        bankLendings: bankLendings.map(lending => ({
                            ...lending,
                            lendingDate: formatDate(lending.lendingDate),
                            endDate: lending.endDate ? formatDate(lending.endDate) : undefined,
                            firstPayDate: lending.firstPayDate ? formatDate(lending.firstPayDate) : undefined
                        })),
                        // 出款人实际利息记录
                        leadExtList: payerInterestList.map(p => ({
                            outMoneyId: p.outMoneyId,
                            outMoneyName: p.outMoneyName,
                            outMoneyAmount: p.outMoneyAmount,
                            creditOutMoneyAmount: p.creditOutMoneyAmount,
                            cardOutMoneyAmount: p.cardOutMoneyAmount,
                            cardPostingOutMoneyAmount: p.cardPostingOutMoneyAmount,
                            borrowOutMoneyAmount: p.borrowOutMoneyAmount,
                            outMoneyCount: p.outMoneyCount,
                            outMoneyCountCk: p.outMoneyCountCk,
                            outMoneyCountGz: p.outMoneyCountGz,
                            interestDays: p.interestDays,
                            interestAmount: p.interestAmount,
                            interestAmountCk: p.interestAmountCk,
                            interestAmountGz: p.interestAmountGz,
                            realInterestAmount: p.realInterestAmount,
                            interestRate: values.interest,
                        }))
                    };

                    setLoading(true);
                    const res:any = await apiClient.request('/release/releaseWithInterest', 'post', null, request);

                    if (res.code == "ok" && res.data) {
                        Toast.success(`释放成功！总利息：${formatAmountZhCn(res.data.totalInterest)}，总抽佣：${formatAmountZhCn(res.data.totalCommission)}`);

                        // 重新加载数据(此时客户已释放，会显示"已释放"状态)
                        await loadData();

                        // 显示释放成功详情预览弹窗
                        setShowPreview(true);
                    } else {
                        Toast.error(res.message || '释放失败');
                    }
                } catch (error) {
                    console.error('释放失败:', error);
                    Toast.error('释放失败');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    /**
     * 处理取消释放(回滚客户释放状态，清除利息数据)
     * 调用链: POST /api/mis/release/cancelRelease
     * → ReleaseLeadController.cancelRelease
     * → ReleaseLeadService.cancelReleaseLead(leadId)
     *   → leadMapper.update: 清空 settledLoanAt/settledLoanOperator, loanStatus→LOANING
     *   → resetInterestByLeadId: 清空 repayment_plan 和 mortgage_loan 的 interestAmount, commissionAmount, endRateDate
     * ← "取消释放成功"
     */
    const handleCancelRelease = async () => {
        Modal.confirm({
            title: '确认取消释放',
            content: '取消释放将清空所有计算数据，确认继续吗？',
            okText: '确认取消',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                try {
                    setLoading(true);
                    const res = await apiClient.request('/release/cancelRelease', 'post', null, {leadId:Number(leadId)});

                    if (res.code == "ok" && res.data) {
                        Toast.success('取消释放成功');
                        // 重新加载数据(此时客户已恢复为 LOANING 状态)
                        await loadData();
                    } else {
                        Toast.error(res.msg || '取消失败');
                    }
                } catch (error) {
                    console.error('取消释放失败:', error);
                    Toast.error('取消释放失败');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // 银行放款数据变化回调(由 BankLendingTable 子组件触发)
    const handleBankLendingsChange = (lendings: any[]) => {
        setBankLendings(lendings);
    };

    // 导出数据(功能待开发)
    const handleExport = () => {
        Toast.info('导出功能开发中...');
    };

    // 返回上一页
    const handleBack = () => {
        navigate(-1);
    };

    // 渲染标签页内容: 'loans'(贷款详情 LoanPlanTable) | 'bank'(银行放款 BankLendingTable)
    const renderTabs = () => {
        const tabs = [
            {
                key: 'reconciliation',
                label: (
                    <Space>
                        <IconCoinMoneyStroked />
                        客户对账
                        <Badge count={summaryData.loanCount} type="danger" />
                    </Space>
                ),
                content: <ReconciliationByLead leadId={leadId} releaseDate={releaseDate}  />
            },
            {
                key: 'loans',
                label: (
                    <Space>
                        <IconCoinMoneyStroked />
                        贷款详情
                        <Badge count={summaryData.loanCount} type="danger" />
                    </Space>
                ),
                // LoanPlanTable: 树形表格，贷款节点可展开显示还款计划子节点
                content: <LoanPlanTable data={loanPlans} loading={loading} />
            },
            {
                key: 'bank',
                label: (
                    <Space>
                        <IconBeaker />
                        银行放款
                        <Badge count={bankLendings.length} type="warning" />
                    </Space>
                ),
                // BankLendingTable: 可编辑表格，支持行内添加/编辑/删除银行放款记录
                content: (
                    <BankLendingTable
                        data={bankLendings}
                        onChange={handleBankLendingsChange}
                        loading={loading}
                    />
                )
            }
        ];

        return tabs.find(tab => tab.key === activeTab)?.content;
    };

    /**
     * 渲染按出款人统计利息表格
     * type: 'ck' 出款利息 | 'gz' 过账利息
     */
    const renderPayerInterestTable = (type: 'ck' | 'gz') => {
        const filteredList = payerInterestList.filter((p: any) => {
            if (type === 'ck') return p.interestAmountCk != null && Number(p.interestAmountCk) !== 0;
            return p.interestAmountGz != null && Number(p.interestAmountGz) !== 0;
        });

        if (filteredList.length === 0) {
            return <Text type="tertiary" style={{ padding: 16, display: 'block', textAlign: 'center' }}>暂无数据</Text>;
        }

        const isCk = type === 'ck';
        const interestKey = isCk ? 'interestAmountCk' : 'interestAmountGz';
        const countKey = isCk ? 'outMoneyCountCk' : 'outMoneyCountGz';
        const amountLabel = isCk ? '出款金额' : '过账金额';
        const countLabel = isCk ? '出款笔数' : '过账笔数';
        const interestLabel = isCk ? '出款利息' : '过账利息';
        const getAmount = (p: any) => isCk
            ? (Number(p.creditOutMoneyAmount || 0) + Number(p.cardOutMoneyAmount || 0) + Number(p.borrowOutMoneyAmount || 0))
            : Number(p.cardPostingOutMoneyAmount || 0);

        return (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid var(--semi-color-border)' }}>
                        <th style={payerTableStyles.th}>出款人</th>
                        <th style={{...payerTableStyles.th, textAlign: 'right'}}>{amountLabel}</th>
                        <th style={{...payerTableStyles.th, textAlign: 'center'}}>{countLabel}</th>
                        <th style={{...payerTableStyles.th, textAlign: 'right'}}>{interestLabel}</th>
                        <th style={{...payerTableStyles.th, textAlign: 'right'}}>实际回款利息</th>
                        <th style={{...payerTableStyles.th, textAlign: 'right'}}>本息汇总</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredList.map((payer: any, idx: number) => {
                        const realInterest = payer.realInterestAmount ? Number(payer.realInterestAmount) : 0;
                        const amount = getAmount(payer);
                        const total = amount + realInterest;
                        const origIdx = payerInterestList.indexOf(payer);
                        return (
                            <tr key={idx} style={{
                                borderBottom: '1px solid var(--semi-color-border)',
                                background: idx % 2 === 0 ? 'var(--semi-color-bg-0)' : 'var(--semi-color-bg-1)',
                            }}>
                                <td style={payerTableStyles.td}>
                                    <Text strong style={{ color: 'var(--semi-color-text-0)' }}>{payer.outMoneyName}</Text>
                                </td>
                                <td style={{...payerTableStyles.td, textAlign: 'right'}}>
                                    <Text>{formatAmountZhCn(amount)}</Text>
                                </td>
                                <td style={{...payerTableStyles.td, textAlign: 'center'}}>
                                    <Tag size="small" color="cyan">{payer[countKey] ?? 0}</Tag>
                                </td>
                                <td style={{...payerTableStyles.td, textAlign: 'right'}}>
                                    <Text style={{ color: '#e91e63' }}>{formatAmountZhCn(payer[interestKey])}</Text>
                                </td>
                                <td style={{...payerTableStyles.td, textAlign: 'right'}}>
                                    <input
                                        type="number"
                                        value={payer.realInterestAmount}
                                        onChange={(e) => {
                                            let newList = [...payerInterestList];
                                            newList[origIdx] = { ...newList[origIdx], realInterestAmount: e.target.value };
                                            setPayerInterestList(newList);
                                        }}
                                        disabled={!!leadInfo?.settledLoanAt}
                                        placeholder="填写实际利息"
                                        style={{...payerTableStyles.input, textAlign: 'right'}}
                                    />
                                </td>
                                <td style={{...payerTableStyles.td, textAlign: 'right'}}>
                                    <Text strong style={{ color: '#1677ff' }}>{formatAmountZhCn(total)}</Text>
                                </td>
                            </tr>
                        );
                    })}
                    <tr style={{ borderTop: '2px solid var(--semi-color-border)', fontWeight: 600 }}>
                        <td style={payerTableStyles.td}><Text strong>合计</Text></td>
                        <td style={{...payerTableStyles.td, textAlign: 'right'}}>
                            <Text strong>{formatAmountZhCn(filteredList.reduce((s, p) => s + getAmount(p), 0))}</Text>
                        </td>
                        <td style={{...payerTableStyles.td, textAlign: 'center'}}>
                            <Tag size="small" color="blue">{filteredList.reduce((s, p) => s + (p[countKey] || 0), 0)}</Tag>
                        </td>
                        <td style={{...payerTableStyles.td, textAlign: 'right'}}>
                            <Text strong style={{ color: '#e91e63' }}>{formatAmountZhCn(filteredList.reduce((s, p) => s + (Number(p[interestKey]) || 0), 0))}</Text>
                        </td>
                        <td style={{...payerTableStyles.td, textAlign: 'right'}}>
                            <Text strong style={{ color: '#00b578' }}>{formatAmountZhCn(filteredList.reduce((s, p) => s + (Number(p.realInterestAmount) || 0), 0))}</Text>
                        </td>
                        <td style={{...payerTableStyles.td, textAlign: 'right'}}>
                            <Text strong style={{ color: '#1677ff' }}>{formatAmountZhCn(filteredList.reduce((s, p) => (s + getAmount(p) + (Number(p.realInterestAmount) || 0)), 0))}</Text>
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    };

    // 未选择客户时的空状态提示
    if (!leadId) {
        return (
            <Card loading={loading}>
                <div className="empty-state">
                    <IconClose size="extra-large" />
                    <Title heading={4}>未选择客户</Title>
                    <Text>请从客户列表中选择一个客户进行释放操作</Text>
                    <Button icon={<IconArrowLeft />} onClick={handleBack}>
                        返回客户列表
                    </Button>
                </div>
            </Card>
        );
    }

    return (
        <div >
            {/* 顶部标题栏: 客户名称 + 已释放标签 + 操作按钮(计算利息/导出/刷新) */}
            <div >
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space>
                            <Button
                                icon={<IconCoinMoneyStroked size='large'/>}
                                onClick={handleBack}
                                theme="borderless"
                            />
                            <Title heading={3}>
                                {leadInfo?.name ? `释放客户：${leadInfo.name}` : '释放客户'}
                            </Title>
                            {/* 已释放客户显示绿色标签 */}
                            {leadInfo?.settledLoanAt && (
                                <Tag color="green" size="large" type="solid">
                                    已释放
                                </Tag>
                            )}
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            {/* 计算利息按钮: 仅未释放时可点击，调用 handleCalculateInterest → POST /release/calculateInterest */}
                            <Button
                                icon={<IconCalendar />}
                                onClick={handleCalculateInterest}
                                disabled={!!leadInfo?.settledLoanAt}
                            >
                                计算利息
                            </Button>
                            <Button
                                icon={<IconDownload />}
                                onClick={handleExport}
                            >
                                导出
                            </Button>
                            {/* 刷新按钮: 重新调用 loadData → GET /release/releaseDetail */}
                            <Button
                                icon={<IconRefresh />}
                                onClick={loadData}
                                loading={loading}
                            >
                                刷新
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* 主体区域: 左侧(客户表单+汇总) + 右侧(贷款/放款信息) */}
            <div >
                <Row gutter={24}>
                    {/* 左侧: 客户信息和表单(span=8) */}
                    <Col span={8}>
                        {/* ReleaseForm: 客户信息表单
                            - 显示客户基本信息(只读): name, leadId
                            - 可编辑字段: 年龄, 婚姻, 日利率, 借款日利率, 过账利率, 抽佣, 释放日期, 经办人, 单位属性等
                            - 已释放时 disabled=true
                            - 通过 onFormApiReady 暴露 formApi 供父组件读取 */}
                        <ReleaseForm
                            leadInfo={leadInfo}
                            onFormApiReady={setReleaseFormApi}
                            disabled={!!leadInfo?.settledLoanAt}
                        />

                        {/* ReleaseSummary: 释放汇总卡片
                            - 显示: 贷款笔数, 计划总数, 总贷款金额, 总已还金额
                            - 计算利息后显示: 总利息, 总抽佣, 总费用 */}
                        <ReleaseSummary
                            summary={summaryData}
                            interestResult={interestResult}
                            loanPlans={loanPlans}
                        />

                        {/* 按出款人统计利息 - 拆分为出款利息/过账利息两个tab */}
                        {payerInterestList.length > 0 && (
                            <Card
                                // title={<Text strong>按出款人统计利息</Text>}
                                style={{ marginTop: 5 }}
                            >
                                <Tabs activeKey={payerInterestTab} onChange={setPayerInterestTab} type="line">
                                    <TabPane tab={`出款利息 (${payerInterestList.filter((p: any) => p.interestAmountCk != null && Number(p.interestAmountCk) !== 0).length})`} itemKey="ck">
                                        {renderPayerInterestTable('ck')}
                                    </TabPane>
                                    <TabPane tab={`过账利息 (${payerInterestList.filter((p: any) => p.interestAmountGz != null && Number(p.interestAmountGz) !== 0).length})`} itemKey="gz">
                                        {renderPayerInterestTable('gz')}
                                    </TabPane>
                                </Tabs>
                            </Card>
                        )}
                    </Col>

                    {/* 右侧: 贷款和银行放款信息(span=16) */}
                    <Col span={16}>
                        <Card
                            title={
                                <Space>
                                    <Text strong>贷款与放款信息</Text>
                                    <Tag>{summaryData.loanCount}笔贷款</Tag>
                                    <Tag>{summaryData.planCount}个计划</Tag>
                                </Space>
                            }
                            headerExtraContent={
                                <Space>
                                    {/* 标签切换: 贷款详情 / 银行放款 */}
                                    <Button
                                        icon={<IconUserCircle />}
                                        type={activeTab === 'reconciliation' ? 'primary' : 'tertiary'}
                                        onClick={() => setActiveTab('reconciliation')}
                                    >
                                        客户对账
                                    </Button>
                                    <Button
                                        icon={<IconCoinMoneyStroked />}
                                        type={activeTab === 'loans' ? 'primary' : 'tertiary'}
                                        onClick={() => setActiveTab('loans')}
                                    >
                                        贷款详情
                                    </Button>
                                    <Button
                                        icon={<IconServer />}
                                        type={activeTab === 'bank' ? 'primary' : 'tertiary'}
                                        onClick={() => setActiveTab('bank')}
                                    >
                                        银行放款
                                    </Button>
                                </Space>
                            }
                            loading={loading}
                        >
                            {/* activeTab='loans' → LoanPlanTable (贷款计划树形表格)
                                activeTab='bank' → BankLendingTable (银行放款记录编辑表格) */}
                            {renderTabs()}
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* 底部操作栏: 客户信息摘要 + 取消释放/确认释放按钮 */}
            <div style={{padding:'10px', marginLeft:'20px'}}>
                <Row justify="space-between" align="middle">
                    {/* <Col>
                        <Space>
                            <Text>客户ID: {leadId}</Text>
                            {leadInfo?.agentName && (
                                <Text>按揭专员: {leadInfo.agentName}</Text>
                            )}
                            {leadInfo?.settledLoanAt && (
                                <Text>释放日期: {formatDate(leadInfo.settledLoanAt)}</Text>
                            )}
                        </Space>
                    </Col> */}
                    <Col>
                        <Space>
                            {/* 取消释放按钮: 仅已释放状态可点击，调用 handleCancelRelease → POST /release/cancelRelease */}
                            <Button
                                style={{width:150}}
                                icon={<IconDelete></IconDelete>}
                                disabled={!leadInfo?.settledLoanAt}
                                loading={loading}
                                theme='solid'
                                size='large'
                                type="danger"
                                onClick={handleCancelRelease}
                            >
                                取消释放
                            </Button>
                            {/* 确认释放按钮: 仅未释放状态可点击，调用 handleRelease → POST /release/releaseWithInterest */}
                            <Button
                                style={{width:150}}
                                icon={<IconCheckCircleStroked />}
                                type="primary"
                                theme='solid'
                                size='large'
                                onClick={handleRelease}
                                disabled={!!leadInfo?.settledLoanAt}
                                loading={loading}
                            >
                                确认释放
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* 利息计算器弹窗(当前为占位实现，实际利息计算通过顶部"计算利息"按钮调用后端) */}
            {/* <InterestCalculator
                visible={showCalculator}
                onClose={() => setShowCalculator(false)}
                onCalculate={handleCalculateInterest}
                loading={loading}
            /> */}

            {/* 释放成功预览弹窗: 显示总利息、总抽佣、各贷款明细 */}
            {showPreview && interestResult && (
                <Modal
                    visible={showPreview}
                    title="释放详情预览"
                    footer={null}
                    onCancel={() => setShowPreview(false)}
                    size="large"
                >
                    <Descriptions>
                        <Descriptions.Item itemKey="总利息">
                            <Text strong type="danger">
                                {formatAmountZhCn(interestResult.totalInterest)}
                            </Text>
                        </Descriptions.Item>
                        <Descriptions.Item itemKey="总抽佣">
                            <Text strong type="success">
                                {formatAmountZhCn(interestResult.totalCommission)}
                            </Text>
                        </Descriptions.Item>
                        <Descriptions.Item itemKey="贷款笔数">
                            {summaryData.loanCount}
                        </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    {/* 各贷款的利息/抽佣明细 */}
                    <Timeline>
                        {interestResult.loanDetails?.map((loan: any, index: number) => (
                            <Timeline.Item key={index}>
                                <Space align="start">
                                    <Text strong>{loan.platform}</Text>
                                    <Tag>{loan.loanType}</Tag>
                                    <Text>利息: {formatAmountZhCn(loan.interest)}</Text>
                                    <Text>抽佣: {formatAmountZhCn(loan.commission)}</Text>
                                </Space>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                </Modal>
            )}
        </div>
    );
};

export default ReleaseLeadPage;

const payerTableStyles: Record<string, React.CSSProperties> = {
    th: {
        textAlign: 'left' as const,
        padding: '8px 4px',
        fontSize: 13,
        color: '#666',
        fontWeight: 'normal' as const,
    },
    td: {
        padding: '8px 4px',
        fontSize: 13,
    },
    input: {
        width: '80px',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        padding: '4px 8px',
        fontSize: 13,
        outline: 'none',
    },
};
